import type { BreakEntry, WorkSegment, BreakCompliance, MealBreakResolution, MealBreakStatus } from '../types';

export function checkBreakCompliance(
  segments: readonly WorkSegment[],
  breaks: readonly BreakEntry[],
  totalPaidMinutes: number,
  mealBreakResolutions: readonly MealBreakResolution[] = [],
): BreakCompliance {
  const totalWorkedHours = totalPaidMinutes / 60;
  const lunchBreaks = breaks.filter((b) => b.type === 'lunch');
  const paidBreaks = breaks.filter((b) => b.type === 'paid_break');

  // Find the earliest clock-in across segments
  const earliestClockIn = segments.reduce(
    (min, s) => (s.clockIn.getTime() < min.getTime() ? s.clockIn : min),
    segments[0]?.clockIn ?? new Date()
  );

  // Meal break: required if shift > 5 hours
  const mealBreakRequired = totalWorkedHours > 5;
  const mealBreakProvided = lunchBreaks.length > 0;

  // Timely = first meal break starts before end of 5th hour from first clock-in
  const fifthHourDeadline = new Date(earliestClockIn.getTime() + 5 * 60 * 60_000);
  const mealBreakTimely =
    mealBreakProvided && lunchBreaks[0].startTime.getTime() < fifthHourDeadline.getTime();

  // Second meal break: required if shift > 10 hours
  const secondMealBreakRequired = totalWorkedHours > 10;
  const secondMealBreakProvided = lunchBreaks.length >= 2;

  // Second meal break timeliness: must start before end of 10th hour
  const tenthHourDeadline = new Date(earliestClockIn.getTime() + 10 * 60 * 60_000);
  const secondMealBreakTimely =
    secondMealBreakProvided && lunchBreaks[1].startTime.getTime() < tenthHourDeadline.getTime();

  // Check resolutions for each meal break
  const firstMealResolution = mealBreakResolutions.find((r) => r.mealBreakNumber === 1);
  const secondMealResolution = mealBreakResolutions.find((r) => r.mealBreakNumber === 2);

  // First meal break violation (ignoring resolutions)
  const firstMealViolation = mealBreakRequired && (!mealBreakProvided || !mealBreakTimely);
  // Second meal break violation (ignoring resolutions)
  const secondMealViolation =
    secondMealBreakRequired && (!secondMealBreakProvided || !secondMealBreakTimely);

  const rawMealBreakPenalty = firstMealViolation || secondMealViolation;

  // Determine meal break status considering resolutions
  const mealBreakStatus = computeMealBreakStatus(
    rawMealBreakPenalty,
    firstMealViolation,
    secondMealViolation,
    firstMealResolution,
    secondMealResolution,
  );

  // Meal penalty after resolutions:
  // Waived = no penalty. Not-provided = penalty stays (documented).
  const mealBreakPenalty = computeMealBreakPenalty(
    firstMealViolation,
    secondMealViolation,
    firstMealResolution,
    secondMealResolution,
  );

  // Rest breaks: 1 per 4 hours worked or major fraction (>2 hours)
  let restBreaksRequired = 0;
  if (totalWorkedHours >= 3.5) {
    restBreaksRequired = Math.floor(totalWorkedHours / 4);
    const remainder = totalWorkedHours % 4;
    if (remainder > 2) restBreaksRequired += 1;
  }
  const restBreaksProvided = paidBreaks.length;
  const restBreakPenalty = restBreaksProvided < restBreaksRequired;

  const details =
    [
      mealBreakPenalty ? 'Meal break violation' : null,
      mealBreakStatus === 'waived' ? 'Meal break waived by employee' : null,
      mealBreakStatus === 'violation_reported' ? 'Meal break violation reported — 1hr premium applied' : null,
      restBreakPenalty ? `Rest break: ${restBreaksProvided}/${restBreaksRequired} provided` : null,
    ]
      .filter(Boolean)
      .join('; ') || null;

  return {
    mealBreakProvided,
    mealBreakTimely: mealBreakProvided ? mealBreakTimely : false,
    secondMealBreakRequired,
    secondMealBreakProvided,
    restBreaksRequired,
    restBreaksProvided,
    mealBreakPenalty,
    restBreakPenalty,
    mealBreakStatus,
    mealBreakDeadline: mealBreakRequired ? fifthHourDeadline : null,
    secondMealBreakDeadline: secondMealBreakRequired ? tenthHourDeadline : null,
    details,
  };
}

function computeMealBreakStatus(
  rawPenalty: boolean,
  firstViolation: boolean,
  secondViolation: boolean,
  firstResolution: MealBreakResolution | undefined,
  secondResolution: MealBreakResolution | undefined,
): MealBreakStatus {
  if (!rawPenalty) return 'compliant';

  // Check if all violations have resolutions
  const firstResolved = !firstViolation || firstResolution != null;
  const secondResolved = !secondViolation || secondResolution != null;

  if (!firstResolved || !secondResolved) return 'unresolved';

  // All violations resolved — determine which type
  const allResolutions = [firstResolution, secondResolution].filter(
    (r): r is MealBreakResolution => r != null,
  );

  const hasNotProvided = allResolutions.some((r) => r.resolution === 'not_provided');
  if (hasNotProvided) return 'violation_reported';

  return 'waived';
}

function computeMealBreakPenalty(
  firstViolation: boolean,
  secondViolation: boolean,
  firstResolution: MealBreakResolution | undefined,
  secondResolution: MealBreakResolution | undefined,
): boolean {
  // First meal break: penalty unless waived
  const firstPenalty =
    firstViolation &&
    !(firstResolution?.resolution === 'waived');

  // Second meal break: penalty unless waived
  const secondPenalty =
    secondViolation &&
    !(secondResolution?.resolution === 'waived');

  return firstPenalty || secondPenalty;
}
